import React, {createContext, useState, useContext, useEffect} from "react"

export const EXAM_CONDITIONS_MIN_LENGTH = 25
const STANDARD_QUIZ_DURATION = 10
const PREVIEW_QUIZ_DURATION = 5
const STANDARD_QUIZ_LENGTH = 25
const PREVIEW_QUIZ_LENGTH = 5
const MAX_LENGTH = 150

const ConfigurationContext = createContext({})

export function use_configuration() {
  const context = useContext(ConfigurationContext)
  if (!context.update_configuration) {
    throw new Error(
      "use_configuration must be wrapped in a <ConfigurationProvider />"
    )
  }
  return context
}

function ConfigurationProvider({
  children,
  quiz_mode,
  quiz,
  exam_conditions
}) {
  const exercise_counts = quiz.question_count || {
    all: 0,
    origins: 0,
    insertions: 0,
    innervations: 0,
    functions: 0
  }
  const length = !quiz.can_train
    ? PREVIEW_QUIZ_LENGTH
    : exam_conditions
      ? EXAM_CONDITIONS_MIN_LENGTH
      : STANDARD_QUIZ_LENGTH
  const initial_data = {
    quiz_mode,
    excluded_muscle_facts: [],
    muscle_facts: "included",
    spelling_exercises: "included",
    duration: quiz.can_train ? STANDARD_QUIZ_DURATION : PREVIEW_QUIZ_DURATION,
    // changes only updated by the user and not when the max length changes
    length: Math.min(length, MAX_LENGTH),
    max_length: MAX_LENGTH,
    exam_conditions,
    can_train: quiz.can_train
  }
  const [configuration, set_configuration] = useState(initial_data)

  useEffect(() => {
    update_configuration({quiz_mode})
  }, [quiz_mode])

  function update_configuration(data) {
    const new_configuration = {...configuration, ...data}
    if (
      data.exam_conditions &&
      configuration.length < EXAM_CONDITIONS_MIN_LENGTH
    ) {
      new_configuration.length = EXAM_CONDITIONS_MIN_LENGTH
    }
    if (
      data.length < Math.min(
        EXAM_CONDITIONS_MIN_LENGTH,
        configuration.max_length
      )
    ) {
      new_configuration.exam_conditions = false
    }
    if (data.quiz_mode && data.quiz_mode !== configuration.quiz_mode) {
      reset_muscle_facts_settings(new_configuration)
    }
    if (data.spelling_exercises === "only") {
      new_configuration.muscle_facts = "excluded"
    }
    if (data.muscle_facts === "only") {
      new_configuration.spelling_exercises = "excluded"
    }
    if (
      configuration.spelling_exercises === "only" &&
      data.spelling_exercises === "included"
    ) {
      new_configuration.muscle_facts = "included"
    }
    if (
      configuration.muscle_facts === "only" &&
      data.muscle_facts === "included"
    ) {
      new_configuration.spelling_exercises = "included"
    }
    if (
      configuration.spelling_exercises === "only" &&
      data.muscle_facts === "included"
    ) {
      new_configuration.spelling_exercises = "included"
    }
    if (
      configuration.muscle_facts === "only" &&
      data.spelling_exercises === "included"
    ) {
      new_configuration.muscle_facts = "included"
    }
    new_configuration.max_length = calc_max_length(new_configuration)
    if (new_configuration.max_length < new_configuration.length) {
      new_configuration.length = new_configuration.max_length
    }
    set_configuration(new_configuration)
  }

  function calc_max_length(configuration) {
    if (!exercise_counts) return

    const excluded_muscle_facts_count =
      configuration.excluded_muscle_facts.reduce(
        (sum, tag) => sum + exercise_counts[tag],
        0
      )
    const muscle_facts_count =
      quiz.muscle_facts_tags.reduce((sum, tag) => sum + exercise_counts[tag], 0)

    if (configuration.spelling_exercises === "only") {
      return exercise_counts.spelling_exercises
    }

    if (configuration.muscle_facts === "only") {
      return muscle_facts_count - excluded_muscle_facts_count
    }

    let length = exercise_counts.all

    if (configuration.spelling_exercises === "excluded") {
      length = length - exercise_counts.spelling_exercises
    }

    if (configuration.muscle_facts === "excluded") {
      length = length - muscle_facts_count
    } else {
      length = length - excluded_muscle_facts_count
    }

    return Math.min(length, MAX_LENGTH)
  }

  function reset_muscle_facts_settings(configuration) {
    configuration.muscle_facts = "included"
    configuration.spelling_exercises = "included"
    configuration.excluded_muscle_facts = []
  }

  return (
    <ConfigurationContext.Provider
      value={{configuration, update_configuration}}
    >
      {children}
    </ConfigurationContext.Provider>
  )
}

export default ConfigurationProvider
